// Feature flags and mode selection for Studio Zara.
// Imported by the host shell, so this file must stay free of the rest of src/zara.

import { useEffect, useState } from 'react';

export const ZARA_PREVIEW_KEY = 'zara.preview';
export const ZARA_MOCK_KEY = 'zara.mock';
export const ZARA_SCENARIO_KEY = 'zara.scenario';

export const ZARA_SCENARIOS = ['empty', 'hub-three-products', 'ingest-blocked', 'statics-mid-run'] as const;
export type ZaraScenarioName = (typeof ZARA_SCENARIOS)[number];

const listeners = new Set<() => void>();

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function isZaraPreview(): boolean {
  return readStorage(ZARA_PREVIEW_KEY) === '1';
}

/** Build flag or the staging preview switch. Neither is set on Netlify today. */
export function isZaraEnabled(): boolean {
  return import.meta.env.VITE_ZARA_ENABLED === 'true' || isZaraPreview();
}

export function isZaraMock(): boolean {
  if (import.meta.env.VITE_ZARA_MOCK === 'true') return true;
  if (readStorage(ZARA_MOCK_KEY) === '1') return true;
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('zaraMock') === '1';
}

export function readZaraScenario(): ZaraScenarioName {
  let raw: string | null = null;
  if (typeof window !== 'undefined') {
    raw = new URLSearchParams(window.location.search).get('zaraScenario');
  }
  if (!raw) raw = readStorage(ZARA_SCENARIO_KEY);
  if (raw && (ZARA_SCENARIOS as readonly string[]).includes(raw)) return raw as ZaraScenarioName;
  return 'hub-three-products';
}

/**
 * Apply `?zara=1` (opt in) or `?zara=0` (opt out) and return the search
 * string with that param removed. Does not navigate; the caller does.
 */
export function consumeZaraPreviewSearch(search: string): { nextSearch: string; changed: boolean } {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const flag = params.get('zara');
  if (flag !== '1' && flag !== '0') {
    return { nextSearch: search, changed: false };
  }
  try {
    if (flag === '1') localStorage.setItem(ZARA_PREVIEW_KEY, '1');
    else localStorage.removeItem(ZARA_PREVIEW_KEY);
  } catch {
    /* storage unavailable */
  }
  params.delete('zara');
  const rest = params.toString();
  return { nextSearch: rest ? `?${rest}` : '', changed: true };
}

export function subscribeZaraPreview(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function emitZaraPreview(): void {
  listeners.forEach((listener) => listener());
}

/** Re-reads the flag when the preview switch changes. */
export function useZaraEnabled(): boolean {
  const [enabled, setEnabled] = useState(isZaraEnabled);
  useEffect(() => subscribeZaraPreview(() => setEnabled(isZaraEnabled())), []);
  return enabled;
}
